import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { COMPANY, PRODUCTS } from "../data/content";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-forest text-cream pt-20 md:pt-28 pb-10 overflow-hidden"
    >
      <div className="grain" />
      <div className="container mx-auto px-6 md:px-10 xl:px-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand */}
          <div className="md:col-span-5 lg:col-span-5">
            <Link to="/" className="flex items-center gap-2" data-testid="footer-logo">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-cream text-forest">
                <Leaf size={18} strokeWidth={1.6} />
              </span>
              <span className="font-serif text-3xl tracking-tight">{COMPANY.short}</span>
            </Link>
            <p className="mt-6 max-w-md text-cream/70 leading-relaxed">
              {COMPANY.tagline}. Cultivating premium mushrooms, saffron and poultry through controlled-environment agriculture in Kolkata, West Bengal.
            </p>
            <p className="mt-6 eyebrow text-cream/60" data-testid="footer-cin">
              CIN · {COMPANY.cin}
            </p>
          </div>

          {/* Products */}
          <div className="md:col-span-3 lg:col-span-2">
            <p className="eyebrow text-cream/60">Products</p>
            <ul className="mt-6 space-y-3">
              {PRODUCTS.map((p) => (
                <li key={p.id}>
                  <Link
                    to={p.slug}
                    className="text-cream/80 hover:text-saffron transition-colors"
                    data-testid={`footer-product-${p.id}`}
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick links */}
          <div className="md:col-span-2 lg:col-span-2">
            <p className="eyebrow text-cream/60">Company</p>
            <ul className="mt-6 space-y-3">
              {[
                { label: "Home", to: "/" },
                { label: "About", to: "/about" },
                { label: "Blog", to: "/blog" },
                { label: "Contact", to: "/contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-cream/80 hover:text-saffron transition-colors"
                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-12 lg:col-span-3">
            <p className="eyebrow text-cream/60">Contact</p>
            <ul className="mt-6 space-y-4 text-cream/80">
              <li className="flex gap-3">
                <MapPin size={16} className="mt-1 text-saffron shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone size={16} className="text-saffron shrink-0" />

                <div className="flex items-center flex-wrap text-cream/80">
                  <a
                    href={COMPANY.phoneHref}
                    className="hover:text-saffron"
                    data-testid="footer-phone"
                  >
                    {COMPANY.phone}
                  </a>

                  <span className="mx-2 opacity-60">|</span>

                  <a
                    href={COMPANY.phoneHref2}
                    className="hover:text-saffron"
                    data-testid="footer-phone"
                  >
                    {COMPANY.phone2}
                  </a>
                </div>
              </li>
              <li className="flex gap-3 items-center">
                <Mail size={16} className="text-saffron shrink-0" />
                <a
                  href={`mailto:${COMPANY.email}`}
                  className="hover:text-saffron break-all"
                  data-testid="footer-email"
                >
                  {COMPANY.email}
                </a>
              </li>
            </ul>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 text-saffron link-underline"
              data-testid="footer-contact-cta"
            >
              Visit our farm <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-cream/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-cream/60">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} {COMPANY.name}. All Rights Reserved.
          </p>
          <p className="text-cream/40" data-testid="footer-crafted-by">
            Crafted with <span className="text-saffron">♡</span> by <a href="https://smeexperts.in" target="_blank" rel="noopener noreferrer" className="font-semibold text-cream/80 hover:text-saffron transition-colors">SME Experts</a>
          </p>
          <div className="flex gap-8">
            <a href="#" className="hover:text-saffron">Terms of Use</a>
            <a href="#" className="hover:text-saffron">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
